import { Spin, useGeneratePath, useIntl, useStaticUrls } from 'platform-components';
import { getExactPathMatch } from 'platform-components/src/utils/getExactPathMatch';
import qs from 'query-string';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';

import { AfishaBannerContainer } from 'common.components/afisha-banner/afisha-banner.container';
import ChangesNotification from 'common.components/changes-notification/changes-notification';

import { TeacherHeader } from 'common.components/TeacherHeader/TeacherHeader';
import { LessonViewWithAddSchoolControl as LessonView } from 'common.components/lesson-view.index/lesson-view_with-add-school-control';
import { getIsTeamBuilderActive } from 'common.components/team-builder/selectors';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';

import { useCurrentGrade } from '../../utils/use-current-grade';
import { DEFAULT_LIBRARY_GRADE } from '../library.common/constants';
import * as libraryActions from '../library.store/actions';

import LibrarySubjectManager from './__subject-manager/library__subject-manager';

import './library.scss';

interface Props {
    savedOrFirstTeamId: number | null;
    canShowClass: boolean;
    isCalendarPlanOpened: boolean;
    isPreview?: boolean | undefined;
}

// eslint-disable-next-line complexity
export const LibraryView = ({ savedOrFirstTeamId, isCalendarPlanOpened, canShowClass, isPreview }: Props) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const staticUrls = useStaticUrls();
    const getRouterPath = useGeneratePath();
    const params = useParams<Record<string, string>>();
    const classId = Number(params.classId);
    const isTeamBuilderActive = useSelector(getIsTeamBuilderActive);

    const isTeacherFromMes = getIsTeacherFromMes(location.search);

    useCurrentGrade(DEFAULT_LIBRARY_GRADE, isPreview);

    const handleCloseModal = () => {
        dispatch(libraryActions.lessonModalClose());
    };

    useEffect(() => {
        // Если класс не указан, пытаемся открыть сохраненный (EDUCATION-19230) или первый из классов
        // Не редиректим, пока team-builder добавляет класс и обновляет getCourses
        if (!classId && savedOrFirstTeamId && !isTeamBuilderActive) {
            let redirectPath = staticUrls.labClassLibrary;
            const { path = '' } = getExactPathMatch(staticUrls, location.pathname) || {};
            const newPath = path.replace('/lab/', '/lab/classes/:classId/');

            if (Object.values(staticUrls).includes(newPath)) {
                redirectPath = newPath;
            }

            if (path === staticUrls.labLibrarySubjectCommonCourseContainer) {
                redirectPath = staticUrls.labLibrarySubjectCollection;
            }

            history.replace(getRouterPath(redirectPath, { classId: savedOrFirstTeamId }) + location.search);
        }
    }, [classId, savedOrFirstTeamId, getRouterPath, history, isTeamBuilderActive, location, staticUrls]);

    const { isRedirectLoggedInUser } = qs.parse(location.search);

    // Redirect to the forbidden page if a user attempts to open
    // a class that isn't yours or is in an archive
    useEffect(() => {
        if (!isRedirectLoggedInUser && classId && !canShowClass && !isPreview) {
            if (typeof window !== 'undefined') {
                window.location.href = getRouterPath(staticUrls.forbidden);
            }
        }
    }, [classId, canShowClass, isPreview, isRedirectLoggedInUser, staticUrls]);

    // The user just received the role and needs to be redirected to the main page
    if (isRedirectLoggedInUser) {
        return <Redirect to={getRouterPath(staticUrls.labLibraryMain)} />;
    }

    if (classId && !canShowClass && !isPreview) {
        return <Spin center />;
    }

    const shouldShowAfishaBanner = !isCalendarPlanOpened;

    return (
        <>
            <Helmet
                title={intl.formatMessage({ id: isPreview ? 'libraryPage.previewList.title' : 'libraryPage.title' })}
            />
            <TeacherHeader />
            <ChangesNotification />
            {isPreview ? (
                <LibrarySubjectManager isPreview={true} />
            ) : (
                <>
                    <LibrarySubjectManager />
                    {shouldShowAfishaBanner && (
                        <div className="library__afisha-banner">
                            <AfishaBannerContainer />
                        </div>
                    )}
                    <LessonView
                        isCopyingEnabled={!isTeacherFromMes}
                        onClose={handleCloseModal}
                        shouldCloseOnGoBack={true}
                    />
                </>
            )}
        </>
    );
};
